import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: "How do I open the dock overlay?",
      answer: "Press Shift+Option+Space (Mac) or Alt+Space (Windows/Linux) on any webpage. A macOS-style dock will appear with your pinned shortcuts. Press Escape or click outside to close.",
    },
    {
      question: "How do I use the command palette?",
      answer: "Press Shift+Option+P (Mac) or Shift+Alt+P (Windows/Linux) to open the command palette directly. Type to search all your shortcuts, use arrow keys to navigate, and press Enter to launch.",
    },
    {
      question: "How do I create a shortcut?",
      answer: "Click the + button in the top right corner. Enter a name (like \"GitHub\" or \"Local Dev\") and the target URL. The name's first letter will be used as the icon if no favicon is available.",
    },
    {
      question: "How do I pin shortcuts to the dock?",
      answer: "Click the pin icon next to any shortcut in your list. Pinned shortcuts appear in the dock overlay. You can have up to 12 pinned shortcuts in your dock.",
    },
    {
      question: "How do I use the omnibox feature?",
      answer: "Type \"go\" in your browser's address bar, press space, then type your shortcut name. Press Enter to navigate. Example: \"go github\" will open your GitHub shortcut.",
    },
    {
      question: "What happens if a site has no favicon?",
      answer: "Local Route automatically generates a colorful icon using the first letter of your shortcut name. For example, \"Glance\" shows \"G\" and \"Admin\" shows \"A\" on a gradient background.",
    },
    {
      question: "What are profiles?",
      answer: "Profiles let you create separate workspaces with different shortcuts. Use them to organize work, personal, and project-specific shortcuts. Switch profiles from the dropdown in the header or press Ctrl+Shift+P.",
    },
    {
      question: "Can I export my shortcuts?",
      answer: "Yes! Go to Settings > Data and click \"Export All Data\". This downloads a JSON file with all your shortcuts, categories, tags, and settings. Import it on another device to sync your setup. (Premium feature)",
    },
    {
      question: "What premium features are available?",
      answer: "Premium includes: unlimited shortcuts, dock items, and profiles, plus health monitoring, custom icon styles and uploads, custom hotkeys, groups & variables, import/export, advanced analytics, and custom themes. One-time payment of $14.99.",
    },
  ];

  return (
    <section id="faq" className="py-24 px-6 bg-muted/30">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-semibold mb-3">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="rounded-lg border border-border bg-card overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
              >
                <span className="font-medium text-sm">{faq.question}</span>
                <ChevronDown 
                  className={cn(
                    "w-4 h-4 text-muted-foreground transition-transform",
                    openIndex === index && "rotate-180"
                  )} 
                />
              </button>
              {openIndex === index && (
                <div className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed animate-fade-in">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>

        <p className="text-center mt-8 text-sm text-muted-foreground">
          Have more questions?{' '}
          <Link to="/contact" className="text-foreground hover:underline underline-offset-4">
            Contact us
          </Link>
        </p>
      </div>
    </section>
  );
};

export default FAQ;
